import { Platform } from 'react-native';
import RNFS from 'react-native-fs';

/**
 * «Живая» аватарка: как понять, что выбранный файл — анимация (заказ владельца 2026-09-05).
 *
 * <p><b>Зачем.</b> Обычный снимок перед отправкой переупаковывается в JPEG
 * (`processAvatarForUpload`), и это правильно: он весит меньше и открывается везде. Но GIF после
 * такой переупаковки — один кадр. Со стороны это выглядело как «аватарка ставится, а анимация
 * не работает»: до сервера просто не доезжало ничего движущегося.
 *
 * <p><b>Почему по байтам, а не по типу из пикера.</b> Тип и расширение — это то, что нам сказали:
 * на Android они приходят из провайдера галереи, на iOS их может не быть вовсе, а «оригинал»
 * из мессенджера легко называется `.jpg`, будучи GIF-ом. Ошибка в любую сторону дорога: приняв
 * за GIF не-GIF, мы отправим на сервер файл, который тот не разберёт; не узнав GIF, вернёмся к
 * той самой неподвижной аватарке. Шесть байт заголовка отвечают на это точно.
 */

/**
 * Потолок на исходную анимацию — тот же, что у сервера
 * (`SquareAvatarImageProcessor.MAX_UPLOAD_BYTES`). Проверяем и здесь, чтобы тяжёлый GIF не
 * уезжал по мобильной сети целиком ради ответа «слишком большой».
 */
export const MAX_ANIMATED_AVATAR_BYTES = 8 * 1024 * 1024;

/** GIF87a / GIF89a — сигнатура формата, первые шесть байт файла. */
const GIF_MAGIC = /^GIF8[79]a/;

/** Сколько байт достаточно прочитать, чтобы узнать формат. */
const MAGIC_BYTES = 6;

function stripFileScheme(uri: string): string {
  return uri.startsWith('file://') ? uri.slice('file://'.length) : uri;
}

/**
 * Дешёвая догадка по ссылке и заявленному типу.
 *
 * <p>Используется как запасной ответ там, где до самих байт не дотянуться (`content://` на
 * Android, ссылка из другого приложения), и как быстрый ответ для `data:`-строк.
 */
export function looksLikeGifSource(uri: string, mimeType?: string | null): boolean {
  const m = (mimeType ?? '').toLowerCase();
  if (m === 'image/gif') return true;
  const clean = uri.split('?')[0].toLowerCase();
  if (clean.startsWith('data:')) return clean.startsWith('data:image/gif');
  return clean.endsWith('.gif');
}

/** Проверка по заголовку файла; при любой неудаче чтения — {@link looksLikeGifSource}. */
export async function isGifSource(uri: string, mimeType?: string | null): Promise<boolean> {
  if (!uri) return false;
  const lower = uri.toLowerCase();
  if (lower.startsWith('data:')) {
    return lower.startsWith('data:image/gif');
  }
  try {
    if (Platform.OS === 'web') {
      const res = await fetch(uri);
      const buf = await res.arrayBuffer();
      return startsWithGifMagic(new Uint8Array(buf.slice(0, MAGIC_BYTES)));
    }
    const head = await RNFS.read(stripFileScheme(uri), MAGIC_BYTES, 0, 'ascii');
    return typeof head === 'string' && GIF_MAGIC.test(head);
  } catch {
    return looksLikeGifSource(uri, mimeType);
  }
}

function startsWithGifMagic(bytes: Uint8Array): boolean {
  if (bytes.length < MAGIC_BYTES) return false;
  let s = '';
  for (let i = 0; i < MAGIC_BYTES; i += 1) {
    s += String.fromCharCode(bytes[i]);
  }
  return GIF_MAGIC.test(s);
}

/**
 * Живые `blob:`-ссылки веба.
 *
 * <p>У `blob:`-адреса нет ни расширения, ни типа: `blob:https://vrmsgr.pro/8e3c…` одинаково
 * выглядит и для снимка, и для анимации. Тип известен ровно в одном месте — там, где кэш
 * получил ответ сервера (`Content-Type`) и создал ссылку, — и оттуда же он сюда и кладётся.
 * Множество маленькое: в нём столько записей, сколько аватарок кэш держит открытыми, и запись
 * уходит вместе с `revokeObjectURL`.
 */
const animatedBlobUrls = new Set<string>();

/** Кэш сообщает: за этой `blob:`-ссылкой лежит анимация. */
export function rememberAnimatedAvatarBlob(objectUrl: string, mimeType: string | null | undefined): void {
  if (!objectUrl) return;
  if ((mimeType ?? '').toLowerCase().split(';')[0].trim() === 'image/gif') {
    animatedBlobUrls.add(objectUrl);
  }
}

/** Ссылку отозвали — забываем и мы. */
export function forgetAnimatedAvatarBlob(objectUrl: string | null | undefined): void {
  if (objectUrl) animatedBlobUrls.delete(objectUrl);
}

/**
 * Уже сохранённый аватар: анимация или нет.
 *
 * <p>Кэш `mediaCache` кладёт скачанный файл под расширением, которое соответствует ответу
 * сервера, а в вебе отдаёт `blob:`-ссылку — её узнаём по {@link rememberAnimatedAvatarBlob}.
 * Разбирать байты здесь незачем и вредно: решение нужно синхронно, в момент отрисовки бейджа.
 */
export function isAnimatedAvatarUri(uri: string | null | undefined): boolean {
  if (!uri) return false;
  if (animatedBlobUrls.has(uri)) return true;
  return looksLikeGifSource(uri, null);
}
